/**
 * Maps an outgoing API request to a demo payload.
 *
 * This is the whole demo-mode surface area. Every `*Api` client shares one
 * axios instance, so answering requests here means the pages themselves need
 * no demo branches at all - `/demo` runs the real page code against synthetic
 * responses, which keeps it honest as a preview of the product rather than a
 * parallel implementation that can drift.
 *
 * A route that is not listed here returns `undefined`, and the caller falls
 * through to the network. That is deliberate: see `adapter.ts`.
 */

import type { TimePeriod } from '@/types/core';
import { demoDate } from './seed';
import { findDemoDeveloper } from './fixture';
import { buildDemoDashboardMetrics, buildDemoMetricTrend } from './generators/metrics';
import { buildDemoReviewNetwork } from './generators/review';
import { buildDemoFileHotspots, buildDemoRiskSummary } from './generators/insights';
import { buildDemoAlerts, buildDemoPullRequests } from './generators/pr';
import { buildDemoHealthScore, buildDemoHealthScoreTrend } from './generators/health';
import { buildDemoInvestmentAllocation, buildDemoIssueMetrics } from './generators/executive';
import { buildDemoDeveloperMetrics } from './generators/developer';
import {
  buildDemoDeveloper,
  buildDemoDeveloperActiveBranches,
  buildDemoDeveloperAnalytics,
  buildDemoDeveloperLanguages,
  buildDemoDeveloperPRActivity,
  buildDemoDeveloperPullRequests,
  buildDemoDeveloperRepositoryContributions,
  buildDemoDeveloperSentiment,
  buildDemoDeveloperSentimentTrends,
  buildDemoDeveloperTestCoverage,
  buildDemoDeveloperWorkBreakdown,
} from './generators/developerDetail';
import {
  buildDemoDataCoverage,
  buildDemoMembership,
  buildDemoDevelopers,
  buildDemoRepositories,
  buildDemoSubscription,
  buildDemoTeams,
} from './generators/org';

/** Query params as the adapter sees them: snake_cased by the request interceptor. */
export type DemoQuery = Record<string, unknown>;

/**
 * "This route is covered, and the answer is that the thing does not exist."
 *
 * Distinct from returning `undefined`, which means "not covered, go to the
 * network". Without it, /demo/developers/dev-nobody fired six real requests at
 * the backend before the page could show its "Failed to load developer" state -
 * the right screen, reached the wrong way.
 */
export const DEMO_NOT_FOUND = Symbol('demo-not-found');

/**
 * Reverse of `getTimePeriodForBackend`.
 *
 * The dashboard endpoint takes a named period rather than a day count, so the
 * generator - which works in days - needs the mapping back. Kept beside the
 * router so the two halves of the conversion stay visible together.
 */
const PERIOD_DAYS: Record<TimePeriod, number> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  quarterly: 90,
  biannual: 180,
  yearly: 365,
};

function num(value: unknown, fallback: number): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Booleans arrive as real booleans from axios params and as strings from a
 * query string, and `Boolean('false')` is `true` - a trap worth naming, since
 * getting it wrong would silently pin the bots toggle to "included".
 */
function bool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

/** The filter state every generator takes, read off one request. */
function filtersFrom(query: DemoQuery, defaultDays = 30) {
  // `days` wins over `period` when both are sent. They describe different
  // things - some endpoints take a window in days AND a bucket size as a named
  // period - so reading `period` first would turn a 90-day health trend into a
  // 7-day one just because its buckets are weekly.
  const period = str(query.period) as TimePeriod | null;
  const days =
    query.days != null
      ? num(query.days, defaultDays)
      : period && period in PERIOD_DAYS
        ? PERIOD_DAYS[period]
        : defaultDays;

  return {
    days,
    // The api layer is inconsistent about which name it sends for a repo
    // scope (`repo_id` on metrics, `repository_id` on risk), so accept both
    // rather than making one page silently ignore the Scope dropdown.
    repoId: str(query.repo_id) ?? str(query.repository_id),
    teamId: str(query.team_id),
    excludeBots: bool(query.exclude_bots, true),
  };
}

/** Filter state for the per-developer routes, which key on the path id. */
function detailFilters(developerId: string, query: DemoQuery) {
  const { days, excludeBots } = filtersFrom(query);
  return { developerId, days, excludeBots };
}

/**
 * Wrap a per-developer handler so an id the fixture does not know answers 404
 * locally rather than falling through to the network.
 */
function forDeveloper<T>(build: (params: ReturnType<typeof detailFilters>) => T) {
  return (match: RegExpMatchArray, query: DemoQuery): T | typeof DEMO_NOT_FOUND => {
    const id = match[1];
    if (!findDemoDeveloper(id)) return DEMO_NOT_FOUND;
    return build(detailFilters(id, query));
  };
}

interface DemoRoute {
  pattern: RegExp;
  handler: (match: RegExpMatchArray, query: DemoQuery) => unknown;
}

const ROUTES: DemoRoute[] = [
  {
    pattern: /^\/metrics\/dashboard\/[^/]+$/,
    handler: (_m, query) => buildDemoDashboardMetrics(filtersFrom(query)),
  },
  {
    pattern: /^\/metrics\/trend\/[^/]+\/([^/?]+)$/,
    handler: (match, query) => buildDemoMetricTrend(match[1], filtersFrom(query)),
  },
  {
    // Order matters: the trend route must be tested before the bare
    // health-score route, or the shorter pattern would need an end anchor it
    // does not have room for.
    pattern: /^\/metrics\/health-score\/trend$/,
    handler: (_m, query) => buildDemoHealthScoreTrend(filtersFrom(query, 90)),
  },
  {
    pattern: /^\/metrics\/health-score$/,
    handler: (_m, query) => buildDemoHealthScore(filtersFrom(query)),
  },
  {
    pattern: /^\/analytics\/review-network\/[^/]+$/,
    handler: (_m, query) => buildDemoReviewNetwork(filtersFrom(query)),
  },
  {
    pattern: /^\/analytics\/data-coverage\/[^/]+$/,
    handler: () => buildDemoDataCoverage(),
  },
  {
    pattern: /^\/analytics\/file-hotspots\/[^/]+$/,
    handler: (_m, query) => buildDemoFileHotspots(filtersFrom(query)),
  },
  {
    pattern: /^\/analytics\/investment-allocation\/[^/]+$/,
    handler: (_m, query) => buildDemoInvestmentAllocation(filtersFrom(query)),
  },
  {
    pattern: /^\/analytics\/issue-metrics\/[^/]+$/,
    handler: (_m, query) => buildDemoIssueMetrics(filtersFrom(query)),
  },
  {
    // Must precede the bare /developers route below, which would otherwise
    // never match this path anyway - but keeping them adjacent makes the
    // ordering dependency visible rather than incidental.
    pattern: /^\/developers\/bulk-metrics$/,
    handler: (_m, query) => buildDemoDeveloperMetrics(filtersFrom(query)),
  },
  {
    // The individual developer page. Every one of its eleven calls is answered,
    // including the eight the page would silently tolerate failing - an
    // uncovered one does not break the page, it goes to the NETWORK carrying a
    // fictional org id, and the demo's guarantee is that nothing leaves the
    // browser.
    //
    // These must precede /developers/bulk-metrics? No: 'bulk-metrics' would
    // also match [^/]+ below, so the literal route is listed FIRST above and
    // this pattern is anchored to exclude it.
    pattern: /^\/developers\/(?!bulk-metrics$)([^/]+)$/,
    handler: forDeveloper(buildDemoDeveloper),
  },
  {
    pattern: /^\/developers\/([^/]+)\/analytics$/,
    handler: forDeveloper(buildDemoDeveloperAnalytics),
  },
  {
    pattern: /^\/developers\/([^/]+)\/test-coverage$/,
    handler: forDeveloper(buildDemoDeveloperTestCoverage),
  },
  {
    pattern: /^\/developers\/([^/]+)\/language-breakdown$/,
    handler: forDeveloper(buildDemoDeveloperLanguages),
  },
  {
    pattern: /^\/developers\/([^/]+)\/work-breakdown$/,
    handler: forDeveloper(buildDemoDeveloperWorkBreakdown),
  },
  {
    pattern: /^\/developers\/([^/]+)\/pr-activity-breakdown$/,
    handler: forDeveloper(buildDemoDeveloperPRActivity),
  },
  {
    pattern: /^\/developers\/([^/]+)\/repository-contributions$/,
    handler: forDeveloper(buildDemoDeveloperRepositoryContributions),
  },
  {
    pattern: /^\/developers\/([^/]+)\/active-branches$/,
    handler: forDeveloper(buildDemoDeveloperActiveBranches),
  },
  {
    pattern: /^\/comments\/developer\/([^/]+)\/sentiment-profile$/,
    handler: forDeveloper(buildDemoDeveloperSentiment),
  },
  {
    pattern: /^\/comments\/developer\/([^/]+)\/sentiment-trends$/,
    handler: forDeveloper(buildDemoDeveloperSentimentTrends),
  },
  {
    pattern: /^\/pull-requests\/developers\/([^/]+)\/pull-requests$/,
    handler: forDeveloper(buildDemoDeveloperPullRequests),
  },
  {
    pattern: /^\/risk\/summary$/,
    handler: (_m, query) => buildDemoRiskSummary(filtersFrom(query)),
  },
  {
    // Populates the Scope dropdown. Without this the filter bar renders empty
    // and the demo reads as a product with no repositories connected.
    pattern: /^\/repositories$/,
    handler: (_m, query) => {
      const repositories = buildDemoRepositories(str(query.team_id));
      return {
        repositories,
        total: repositories.length,
        page: num(query.page, 1),
        page_size: num(query.page_size, 50),
      };
    },
  },
  {
    pattern: /^\/teams$/,
    handler: () => buildDemoTeams(),
  },
  {
    pattern: /^\/members\/me$/,
    handler: () => buildDemoMembership(),
  },
  {
    pattern: /^\/pull-requests\/organizations\/[^/]+\/pull-requests$/,
    handler: (_m, query) =>
      buildDemoPullRequests({
        ...filtersFrom(query),
        minWaitHours: query.min_wait_hours == null ? undefined : num(query.min_wait_hours, 0),
      }),
  },
  {
    pattern: /^\/alerts$/,
    handler: (_m, query) => buildDemoAlerts(filtersFrom(query)),
  },
  {
    pattern: /^\/developers$/,
    handler: (_m, query) => {
      const developers = buildDemoDevelopers(bool(query.exclude_bots, true));
      return { developers, total: developers.length };
    },
  },
  {
    // Feature gating. An anonymous visitor has no subscription, so without
    // this every premium page renders an upgrade wall instead of the feature
    // the demo exists to show.
    pattern: /^\/subscriptions\/(?!pricing$)[^/]+$/,
    handler: () => buildDemoSubscription(),
  },
  {
    // Answered deliberately, and deliberately empty. `Layout` fetches this for
    // any org in scope, and a developer-satisfaction survey modal appearing
    // over a public demo would ask a stranger to rate a team that does not
    // exist. Returning "nothing active" is also what stops the request falling
    // through to a 401 the visitor never needed to generate.
    pattern: /^\/space\/surveys\/[^/]+\/active$/,
    handler: () => ({
      period: 'monthly',
      period_start: demoDate(30),
      period_end: demoDate(0),
      questions: [],
      already_responded: true,
      response_count: 0,
    }),
  },
];

/**
 * Resolve a demo payload for a request, or `undefined` if the route is not
 * covered.
 *
 * @param url  Path relative to the api base, e.g. `/metrics/dashboard/demo-org`.
 * @param query Params already snake_cased by the axios request interceptor.
 */
export function resolveDemoResponse(url: string, query: DemoQuery = {}): unknown | undefined {
  // Strip any inline query string: some callers build the URL by hand, and
  // merging those params in means the filters still reach the generator.
  const [path, inlineQuery] = url.split('?');
  const merged: DemoQuery = inlineQuery
    ? { ...Object.fromEntries(new URLSearchParams(inlineQuery)), ...query }
    : query;

  for (const route of ROUTES) {
    const match = path.match(route.pattern);
    if (match) return route.handler(match, merged);
  }
  return undefined;
}

/** Exposed for the coverage test, which pins the routes the demo pages need. */
export const DEMO_ROUTE_PATTERNS = ROUTES.map((r) => r.pattern);
